import { useEffect, useState } from "react";
import { MapPin, Mail, Phone, Send, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Listen for prefill events from Memberships tier CTAs
  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message;
      if (!msg) return;
      setForm((f) => ({ ...f, message: msg }));
      // Focus the name field so the user can start typing right away
      setTimeout(() => {
        const nameEl = document.querySelector('[data-testid="contact-name-input"]');
        if (nameEl) nameEl.focus();
      }, 400);
    };
    window.addEventListener("bh:prefill-contact", handler);
    return () => window.removeEventListener("bh:prefill-contact", handler);
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#050505] py-20 md:py-32 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
            [ 05 — Contact ]
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.0]">
            Let's Build
            <br />
            <span className="text-[#2563EB]">Your Game.</span>
          </h2>
          <p className="mt-6 text-[#A1A1AA] text-base md:text-lg max-w-md leading-relaxed">
            Reach out about training, memberships, scheduling, or anything else.
            We respond to every athlete personally.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#60A5FA]">
                <MapPin size={16} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Location
                </div>
                <div className="mt-1 text-white font-medium">
                  Greenville / Taylors, South Carolina
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#60A5FA]">
                <Mail size={16} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Email
                </div>
                <a
                  href="mailto:bball4life406@gmail.com"
                  className="mt-1 text-white font-medium hover:text-[#60A5FA] block"
                >
                  bball4life406@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#60A5FA]">
                <Phone size={16} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Phone
                </div>
                <a
                  href="tel:8647047496"
                  className="mt-1 text-white font-medium hover:text-[#60A5FA] block"
                >
                  (864) 704-7496
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-[#60A5FA]">
                <Clock size={16} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Hours
                </div>
                <div className="mt-1 text-white font-medium">
                  Mon – Fri · 12 PM – 5 PM
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="bg-[#070B14] border border-white/5 p-8 md:p-10"
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA] mb-6">
            [ Send a Message ]
          </div>

          <div className="space-y-5">
            <Field label="Name">
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                data-testid="contact-name-input"
                className="bh-input"
                placeholder="Full name"
                required
              />
            </Field>
            <Field label="Email">
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                data-testid="contact-email-input"
                className="bh-input"
                placeholder="you@email.com"
                required
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                data-testid="contact-phone-input"
                className="bh-input"
                placeholder="(555) 555-5555"
              />
            </Field>
            <Field label="Message">
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                data-testid="contact-message-input"
                className="bh-input min-h-[140px] resize-y"
                placeholder="Tell us about the athlete and training goals…"
                required
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-bold tracking-widest uppercase text-sm px-8 py-4 rounded-sm transition-colors"
          >
            {submitting ? "Sending…" : "Send Message"}
            <Send size={16} />
          </button>
        </form>
      </div>

      <style>{`
        .bh-input {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.08);
          color: #fff;
          padding: 14px 16px;
          font-size: 14px;
          outline: none;
          transition: border-color 200ms ease;
          border-radius: 2px;
          font-family: inherit;
        }
        .bh-input:focus {
          border-color: #2563EB;
        }
        .bh-input::placeholder {
          color: rgba(255,255,255,0.3);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default Contact;
